import React, {FC, useState} from "react";
import {Link} from "react-router-dom";
import {BellSlashIcon, ChevronRightIcon} from "@heroicons/react/24/outline";
import {FollowType} from "data/types";
import {useGetFollowsQuery} from "store/api";
import Avatar from "shared/Avatar/Avatar";
import ButtonSecondary from "shared/Button/ButtonSecondary";

const PAGE_SIZE = 12;

/**
 * The organizers the customer follows, most recent first. "Ver más" asks
 * for a longer first page, so following or unfollowing elsewhere refreshes
 * the whole list at once.
 */
export const FollowedOrganizers: FC = () => {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const {data, isFetching, isLoading} = useGetFollowsQuery({limit});

  const follows: FollowType[] = data?.data ?? [];
  const total: number = data?.total ?? 0;

  return (
    <div className="listingSection__wrap">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-2xl font-semibold">Organizadores que sigues</h2>
        {total > 0 && (
          <span className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums">
            {total}
          </span>
        )}
      </div>
      <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex animate-pulse items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-neutral-200 dark:bg-neutral-800" />
              <div className="h-4 w-40 rounded bg-neutral-200 dark:bg-neutral-800" />
            </div>
          ))}
        </div>
      ) : follows.length === 0 ? (
        <p className="text-neutral-500 dark:text-neutral-400">
          Aún no sigues a ningún organizador. Entra a un evento y sigue a quien
          lo organiza para enterarte de sus próximos eventos.
        </p>
      ) : (
        <>
          <ul className="grid gap-2 sm:grid-cols-2">
            {follows.map((follow) => (
              <li key={follow._id}>
                <Link
                  to={`/organizer/${follow.target._id}`}
                  className="group flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <Avatar
                    sizeClass="w-12 h-12 text-lg"
                    imgUrl={follow.target.img}
                    userName={follow.target.businessName}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {follow.target.businessName}
                  </span>
                  {!follow.notify && (
                    <BellSlashIcon
                      className="w-4 h-4 shrink-0 text-neutral-400"
                      aria-label="Notificaciones silenciadas"
                    />
                  )}
                  <ChevronRightIcon className="w-4 h-4 shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </li>
            ))}
          </ul>
          {follows.length < total && (
            <div className="flex justify-center">
              <ButtonSecondary
                loading={isFetching}
                disabled={isFetching}
                onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
              >
                Ver más
              </ButtonSecondary>
            </div>
          )}
        </>
      )}
    </div>
  );
};
